import React from 'react';
import { observer } from 'mobx-react';
import { Pagination } from 'ats/components/pagination/Pagination';
import TaskCard from './TaskCard';
import HistoryCard from './HistoryCard';
import EmptyState from 'ats/components/empty-state/EmptyState';
import { EMPTY_STATE_ICONS } from 'ats/components/empty-state/emptyStateIcons';
import { applicantHistoryStore } from '../ApplicantHistoryStore';
import { eventManager } from 'ats/utils/EventManager';
import { ListStore } from 'shared/stores/ListStore';
import AtsTableSkeleton from 'ats/components/ats-table-skeleton/AtsTableSkeleton';
import PinnedTasksPanel from 'ats/components/pinned-tasks-panel/PinnedTasksPanel';
import { PIN_SCOPES } from 'ats/utils/taskPin';
import {
	TASK_FOCUS_EVENT,
	focusTimelineTask,
	isFocusRequestFor
} from 'ats/utils/timelineTaskFocus';

@observer
export class ApplicantInternalProcess extends React.Component {
	constructor(props) {
		super(props);
		this.listStore = new ListStore(applicantHistoryStore, {
			entityName: 'Applicant History'
		});
		this.listStore.beginLoading();
		this.timelineRef = React.createRef();
	}

	loadApplicantHistory = applicant => {
		if (!applicant?.id) return;

		applicantHistoryStore.setQuery('applicantId', applicant.id);
		applicantHistoryStore.setQuery('sort', {
			column: 'createdAt',
			asc: false
		});
		this.listStore.search();
	};

	componentDidMount() {
		eventManager.on('applicantStageChanged', this.handleApplicantStageChanged);
		eventManager.on('taskChanged', this.handleTaskChanged);
		eventManager.on('taskPinChanged', this.handleTaskChanged);
		eventManager.on(TASK_FOCUS_EVENT, this.handleTaskFocusRequested);
		this.loadApplicantHistory(this.props.applicant);
	}

	componentDidUpdate(prevProps) {
		const { applicant } = this.props;
		if (prevProps.applicant?.id !== applicant?.id && applicant?.id) {
			this.loadApplicantHistory(applicant);
		}
	}

	handleApplicantStageChanged = ({ applicantId } = {}) => {
		if (String(applicantId) === String(this.props.applicant?.id)) {
			this.listStore.search();
		}
	};

	handleTaskChanged = () => {
		if (this.props.applicant?.id) {
			this.listStore.search();
		}
	};

	componentWillUnmount() {
		eventManager.off('applicantStageChanged', this.handleApplicantStageChanged);
		eventManager.off('taskChanged', this.handleTaskChanged);
		eventManager.off('taskPinChanged', this.handleTaskChanged);
		eventManager.off(TASK_FOCUS_EVENT, this.handleTaskFocusRequested);
	}

	handleTaskFocusRequested = request => {
		if (
			!isFocusRequestFor(
				request,
				PIN_SCOPES.APPLICANT,
				this.props.applicant?.id
			)
		) {
			return;
		}
		focusTimelineTask({
			container: this.timelineRef.current,
			listStore: this.listStore,
			taskId: request.taskId
		});
	};

	handlePaginationChange = value => {
		this.listStore.handlePaginationChange(
			value,
			this.listStore.store.query.pagination.limit
		);
	};

	render() {
		const isEmpty = !this.listStore.loading && this.listStore.data.length === 0;
		const isInitialLoading =
			this.listStore.loading && this.listStore.data.length === 0;

		return (
			<div className="activity-with-pinned">
				<div className="activity-with-pinned-main" ref={this.timelineRef}>
					{this.renderTimeline(isEmpty, isInitialLoading)}
				</div>
				{!isEmpty && !isInitialLoading ? (
					<PinnedTasksPanel
						scope={PIN_SCOPES.APPLICANT}
						entityId={this.props.applicant?.id}
					/>
				) : null}
			</div>
		);
	}

	renderTimeline(isEmpty, isInitialLoading) {
		return (
			<div className="applicant-internal-process">
				{isInitialLoading ? (
					<table className="table is-fullwidth">
						<AtsTableSkeleton columns={1} rows={4} />
					</table>
				) : isEmpty ? (
					<div className="applicant-profile-tab-empty applicant-profile-tab-empty--internal-process">
						<EmptyState
							icon={EMPTY_STATE_ICONS.internalProcess}
							iconAlt="No internal process activity"
							message="This candidate hasn't started any internal process yet."
							actionsInText
							actionText="Assign to a Job"
							onActionClick={this.props.onAssignToJob}
						/>
					</div>
				) : this.listStore.data.length > 0 ? (
					<>
						<div className="applicant-internal-process-list">
							{this.listStore.data.map(item =>
								item.source === 'task' ? (
									<TaskCard
										key={`task-${item.id}`}
										task={item}
										onSuccess={() => {
											this.listStore.search();
										}}
									/>
								) : (
									<HistoryCard
										key={`history-${item.id}`}
										history={item}
										onSuccess={() => {
											this.listStore.search();
										}}
									/>
								)
							)}
						</div>
						<Pagination
							loading={this.listStore.loading}
							pagination={this.listStore.store.query.pagination}
							onPaginationChange={this.handlePaginationChange}
						/>
					</>
				) : null}
			</div>
		);
	}
}
